import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import type { CollectableFeeRow, CollectedFeeRow } from '@klear/collection'
import type { Fee } from '@klear/fee'
import { ROUTE_COLLECTIONS } from '@klear/constants'
import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'
import type { Money } from '@klear/money'
import {
  useCollectableFees,
  useCollectedFees,
  useCollectableAdvanceRecoveries,
  useCollectionMutations
} from '@klear/ui/hooks/use-collections'
import isFinite from 'lodash/isFinite'
import toNumber from 'lodash/toNumber'
import { KlearDate } from '@klear/date'
import {
  COLLECTION_QUERY_COLLECTION_TYPE,
  COLLECTION_QUERY_REFERENCE_ID,
  COLLECTION_QUERY_TRADER_ID,
  COLLECTION_TYPE,
  getCollectionStepOrder,
  type CollectionType,
  type CollectionStepId
} from '@/app/collections/steps'
import { PAYMENT_TYPE_COLLECTION_FEE, PAYMENT_TYPE_COLLECTION_RECOVERY } from '@klear/payment/constants'

const PAYMENT_TYPE_BY_COLLECTION_TYPE: Record<CollectionType, string> = {
  [COLLECTION_TYPE.FEES]: PAYMENT_TYPE_COLLECTION_FEE,
  [COLLECTION_TYPE.ADVANCE_RECOVERY]: PAYMENT_TYPE_COLLECTION_RECOVERY
}

export type PaymentDetailsState = {
  paymentNetwork: string
  source: {
    type: string
    id: number
    organizationId: number
    organizationName: string
    name?: string
  }
  destination: {
    type: string
    id: number
    organizationId: number
    organizationName: string
    name?: string
  }
}

export type CollectionState = {
  collectionType: CollectionType | null
  traderId: number | null
  selectedFees: { feeId: number; money: Money }[]
  selectedRecoveries: { orderEventId: number; money: Money; endDate: string | null }[]
  paymentDetails: PaymentDetailsState | null
  paymentIds: number[]
}

const defaultState: CollectionState = {
  collectionType: null,
  traderId: null,
  selectedFees: [],
  selectedRecoveries: [],
  paymentDetails: null,
  paymentIds: []
}

export type CollectedFeeWithDate = { fee: Fee; collectedAt: string }

type CollectableRecoveryWire = RouterOutputs['collection']['listCollectableAdvanceRecoveries'][number]

export type CollectableRecovery = Omit<CollectableRecoveryWire, 'collectableAmount'> & { totalAmount: Money }

export type CollectionOutletContext = {
  state: CollectionState
  setState: React.Dispatch<React.SetStateAction<CollectionState>>
  stepOrder: CollectionStepId[]
  isEarlyAdvanceRecovery: boolean
  collectableFees: Fee[]
  collectableFeeRows: CollectableFeeRow[]
  collectedFeesWithDates: CollectedFeeWithDate[]
  collectableRecoveries: CollectableRecovery[]
  invoiceOfferDisplayMap: Map<number, string>
  isCollectableLoading: boolean
  includeNotDueRecoveries: boolean
  setIncludeNotDueRecoveries: (value: boolean) => void
  collect: () => Promise<void>
  isCollecting: boolean
  highlightReferenceId: number | null
}

export type CollectionStepCtx = CollectionOutletContext

const CollectionContext = createContext<CollectionOutletContext | null>(null)

const VALID_DEEP_LINK_TYPES: CollectionType[] = [COLLECTION_TYPE.FEES, COLLECTION_TYPE.ADVANCE_RECOVERY]

function buildInitialState(searchParams: URLSearchParams): CollectionState {
  const ct = searchParams.get(COLLECTION_QUERY_COLLECTION_TYPE) as CollectionType | null
  const collectionType = ct != null && VALID_DEEP_LINK_TYPES.includes(ct) ? ct : null
  const tRaw = searchParams.get(COLLECTION_QUERY_TRADER_ID)
  const tid = tRaw ? toNumber(tRaw) : NaN
  const traderId = isFinite(tid) && tid > 0 ? tid : null
  return { ...defaultState, collectionType, traderId }
}

function parseHighlightReferenceId(searchParams: URLSearchParams): number | null {
  const raw = searchParams.get(COLLECTION_QUERY_REFERENCE_ID)
  const id = raw ? toNumber(raw) : NaN
  return isFinite(id) && id > 0 ? id : null
}

export function CollectionWizardLayout({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<CollectionState>(() => buildInitialState(searchParams))
  const [includeNotDueRecoveries, setIncludeNotDueRecoveries] = useState(false)
  const highlightReferenceId = useMemo(() => parseHighlightReferenceId(searchParams), [searchParams])

  const isEarlyAdvanceRecovery = useMemo(() => {
    if (state.collectionType !== COLLECTION_TYPE.ADVANCE_RECOVERY) return false
    const today = KlearDate.today()
    return state.selectedRecoveries.some((r) => {
      const endDate = KlearDate.parse(r.endDate)
      return endDate != null && today.isBefore(endDate)
    })
  }, [state.collectionType, state.selectedRecoveries])

  const stepOrder = useMemo(
    () => getCollectionStepOrder(state.collectionType, { includeRecalculateAllocation: isEarlyAdvanceRecovery }),
    [state.collectionType, isEarlyAdvanceRecovery]
  )

  const isFeeCollection = state.collectionType === COLLECTION_TYPE.FEES
  const isAdvanceRecovery = state.collectionType === COLLECTION_TYPE.ADVANCE_RECOVERY
  const hasTrader = state.traderId != null
  const traderFilters = state.traderId != null ? { traderId: state.traderId } : undefined

  const collectableFeesQuery = useCollectableFees(traderFilters ?? {}, isFeeCollection && hasTrader)
  const collectableFeeRows = useMemo<CollectableFeeRow[]>(
    () => collectableFeesQuery.data ?? [],
    [collectableFeesQuery.data]
  )
  const collectableFees = useMemo(() => collectableFeeRows.map((row) => row.fee), [collectableFeeRows])

  const collectedFeesQuery = useCollectedFees(traderFilters ?? {}, isFeeCollection && hasTrader)
  const collectedFeeRows = useMemo<CollectedFeeRow[]>(() => collectedFeesQuery.data ?? [], [collectedFeesQuery.data])
  const collectedFeesWithDates = useMemo<CollectedFeeWithDate[]>(
    () =>
      collectedFeeRows
        .filter((row): row is CollectedFeeRow & { collectedAt: string } => row.collectedAt != null)
        .map(({ fee, collectedAt }) => ({ fee, collectedAt })),
    [collectedFeeRows]
  )

  const invoiceOfferDisplayMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const { invoiceOffer } of [...collectableFeeRows, ...collectedFeeRows]) {
      if (invoiceOffer != null) map.set(invoiceOffer.id, invoiceOffer.idDisplay)
    }
    return map
  }, [collectableFeeRows, collectedFeeRows])

  const collectableRecoveriesQuery = useCollectableAdvanceRecoveries(traderFilters ?? {}, {
    includeNotDue: includeNotDueRecoveries,
    enabled: isAdvanceRecovery && hasTrader
  })

  const collectableRecoveries = useMemo<CollectableRecovery[]>(
    () =>
      (collectableRecoveriesQuery.data ?? []).map(({ collectableAmount, ...row }) => ({
        ...row,
        totalAmount: collectableAmount
      })),
    [collectableRecoveriesQuery.data]
  )

  const isCollectableLoading =
    (isFeeCollection && collectableFeesQuery.isLoading) || (isAdvanceRecovery && collectableRecoveriesQuery.isLoading)

  const utils = trpc.useUtils()
  const { collectFees, collectRecoveries, isCollecting } = useCollectionMutations()

  const collect = useCallback(async () => {
    const selectedFeeIdSet = new Set(state.selectedFees.map((f) => f.feeId))
    const affectedInvoiceOfferIds = isFeeCollection
      ? [
          ...new Set(
            collectableFeeRows
              .filter((row) => row.fee.id != null && selectedFeeIdSet.has(row.fee.id))
              .map((row) => row.invoiceOffer?.id)
              .filter((id): id is number => id != null)
          )
        ]
      : state.selectedRecoveries.map((r) => r.orderEventId)

    try {
      let ids: number[] = []
      if (isFeeCollection) {
        const [firstFee, ...restFees] = state.selectedFees.map((f) => ({
          orderEventId: f.feeId,
          money: f.money.toJSON()
        }))
        if (!firstFee) return
        const result = await collectFees({ items: [firstFee, ...restFees] })
        ids = result?.paymentIds ?? []
      } else if (isAdvanceRecovery) {
        const [firstRecovery, ...restRecoveries] = state.selectedRecoveries.map((r) => ({
          orderEventId: r.orderEventId,
          money: r.money.toJSON()
        }))
        if (!firstRecovery) return
        const result = await collectRecoveries({ items: [firstRecovery, ...restRecoveries] })
        ids = result?.paymentIds ?? []
      }
      setState((prev) => ({ ...prev, paymentIds: ids }))
      await Promise.all([
        utils.collection.listCollectableFees.invalidate(),
        utils.collection.listCollectedFees.invalidate(),
        utils.collection.listCollectableAdvanceRecoveries.invalidate(),
        utils.collection.listCollectionPayments.invalidate(),
        ...affectedInvoiceOfferIds.map((id) => utils.invoiceOffer.get.invalidate({ invoiceOfferId: id }))
      ])
      const filterForType = PAYMENT_TYPE_BY_COLLECTION_TYPE[state.collectionType ?? COLLECTION_TYPE.FEES]
      const params = new URLSearchParams({ filter: filterForType })
      navigate(`/${ROUTE_COLLECTIONS}?${params.toString()}`)
    } catch {}
  }, [
    state.collectionType,
    state.selectedFees,
    state.selectedRecoveries,
    isFeeCollection,
    isAdvanceRecovery,
    collectableFeeRows,
    collectFees,
    collectRecoveries,
    utils,
    setState,
    navigate
  ])

  const contextValue = useMemo<CollectionOutletContext>(
    () => ({
      state,
      setState,
      stepOrder,
      isEarlyAdvanceRecovery,
      collectableFees,
      collectableFeeRows,
      collectedFeesWithDates,
      collectableRecoveries,
      invoiceOfferDisplayMap,
      isCollectableLoading,
      includeNotDueRecoveries,
      setIncludeNotDueRecoveries,
      collect,
      isCollecting,
      highlightReferenceId
    }),
    [
      state,
      setState,
      stepOrder,
      isEarlyAdvanceRecovery,
      collectableFees,
      collectableFeeRows,
      collectedFeesWithDates,
      collectableRecoveries,
      invoiceOfferDisplayMap,
      isCollectableLoading,
      includeNotDueRecoveries,
      collect,
      isCollecting,
      highlightReferenceId
    ]
  )

  return <CollectionContext.Provider value={contextValue}>{children}</CollectionContext.Provider>
}

export function useCollectionOutletContext(): CollectionOutletContext {
  const ctx = useContext(CollectionContext)
  if (!ctx) throw new Error('useCollectionOutletContext must be used within CollectionWizardLayout')
  return ctx
}
